import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import {
  ContactGroupAssignMode,
  ContactGrouopService,
} from 'src/app/private/services/contact-groups.service';
import { TrackingService } from 'src/app/private/services/tracking.service';
import { ContactGroupOption, normalizeExtraFields } from 'src/app/utils/contact-group-batch.util';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import {
  CONTACT_GROUP_TYPE_OPTIONS,
  ContactGroupType,
} from 'src/app/utils/contact-group-type.util';

@Component({
  selector: 'app-create-contact-group-from-contacts',
  templateUrl: './create-contact-group-from-contacts.component.html',
  styleUrls: ['./create-contact-group-from-contacts.component.scss'],
})
export class CreateContactGroupFromContactsComponent implements OnInit {
  @Input() selectedContacts: any[] = [];
  @Output() close = new EventEmitter<void>();
  @Output() reload = new EventEmitter<void>();

  btnLoading = false;
  groupsLoading = false;
  showNewGroupForm = false;
  contactGroups: ContactGroupOption[] = [];
  selectedGroupId: number | null = null;
  groupName = '';
  group_type: ContactGroupType = 'both';
  groupTypeOptions = CONTACT_GROUP_TYPE_OPTIONS;
  batchProgress = '';
  isAdmin = false;

  constructor(
    private toaster: ToastrService,
    private contactGroupService: ContactGrouopService,
    private localStorage: LocalStorageService,
    private tracker: TrackingService
  ) {
    this.isAdmin = this.localStorage.checkFullAccessForTableList(
      this.localStorage.getRole()
    );
  }

  ngOnInit(): void {
    this.loadContactGroups();
  }

  get selectedCount(): number {
    return this.selectedContacts?.length ?? 0;
  }

  get groupMode(): ContactGroupAssignMode {
    return this.showNewGroupForm ? 'new' : 'existing';
  }

  get submitLabel(): string {
    if (!this.btnLoading) {
      return this.showNewGroupForm ? 'Create Group' : 'Add to Group';
    }
    return this.batchProgress || 'Saving...';
  }

  get canSubmit(): boolean {
    if (this.btnLoading || this.selectedCount < 1) {
      return false;
    }
    if (this.showNewGroupForm) {
      return !!this.groupName.trim();
    }
    return !!this.selectedGroupId && !this.groupsLoading;
  }

  closeModal(): void {
    this.close.emit();
  }

  openNewGroupForm(): void {
    this.showNewGroupForm = true;
    this.groupName = '';
  }

  useExistingGroup(): void {
    this.showNewGroupForm = false;
    if (!this.selectedGroupId && this.contactGroups.length) {
      this.selectedGroupId = this.contactGroups[0].id;
    }
  }

  private loadContactGroups(): void {
    this.groupsLoading = true;
    this.contactGroupService
      .fetchContactGroupsForSelect({
        clientId: this.localStorage.getClientId(),
        centerId: this.localStorage.getCenterId(),
        userId: this.localStorage.getUserId(),
        isAdmin: this.isAdmin,
      })
      .subscribe({
        next: (groups) => {
          this.contactGroups = groups;
          this.groupsLoading = false;
          this.showNewGroupForm = false;
          this.selectedGroupId = groups.length ? groups[0].id : null;
        },
        error: () => {
          this.groupsLoading = false;
          this.showNewGroupForm = false;
          this.selectedGroupId = null;
          this.toaster.error('Failed to load contact groups.');
        },
      });
  }

  submit(): void {
    if (!this.selectedContacts?.length) {
      this.toaster.error('Please select at least one contact.');
      return;
    }

    if (!this.showNewGroupForm && !this.selectedGroupId) {
      this.toaster.error('Please select a contact group.');
      return;
    }

    if (this.showNewGroupForm && !this.groupName.trim()) {
      this.toaster.error('Please enter a Contact Group Name.');
      return;
    }

    const contacts = this.selectedContacts
      .map((contact) => this.mapContactToGroupEntry(contact))
      .filter((c) => c.email || c.phone);

    if (!contacts.length) {
      this.toaster.error(
        'Selected contacts must have at least an email or phone number.'
      );
      return;
    }

    this.btnLoading = true;
    this.batchProgress = this.showNewGroupForm
      ? 'Creating group...'
      : 'Adding to group...';
    const clientId = this.localStorage.getClientId();
    const groupName = this.groupName.trim();
    const selectedGroup = this.contactGroups.find(
      (g) => g.id === this.selectedGroupId
    );
    const targetGroupName = this.showNewGroupForm
      ? groupName
      : selectedGroup?.name || 'group';

    let lastAdded = 0;
    this.contactGroupService
      .assignMembersToContactGroup({
        mode: this.groupMode,
        existingGroupId: this.selectedGroupId,
        groupName,
        group_type: this.group_type,
        contacts,
        clientId,
        centerId: this.localStorage.getCenterId(),
        userId: this.localStorage.getUserId(),
        isAdmin: this.isAdmin,
      })
      .subscribe({
        next: (progress) => {
          lastAdded = progress.added;
          this.batchProgress = `Adding contacts ${progress.added}/${progress.total}...`;
        },
        complete: () => {
          const action = this.showNewGroupForm ? 'created with' : 'added to';
          this.toaster.success(
            `${contacts.length} contact(s) ${action} "${targetGroupName}"!`
          );
          this.tracker.track(
            'Create',
            'Contact Group',
            `${this.showNewGroupForm ? 'Created' : 'Added'} ${contacts.length} contacts in group "${targetGroupName}"`
          );
          this.btnLoading = false;
          this.batchProgress = '';
          this.reload.emit();
          this.closeModal();
        },
        error: (err) => {
          const partialMsg =
            lastAdded > 0
              ? ` Added ${lastAdded} of ${contacts.length} before the error.`
              : '';
          this.toaster.error(
            (err?.error?.message ||
              err?.message ||
              'Failed to save contacts to contact group.') + partialMsg
          );
          this.btnLoading = false;
          this.batchProgress = '';
          if (lastAdded > 0) {
            this.reload.emit();
          }
        },
      });
  }

  private mapContactToGroupEntry(contact: any): {
    name: string;
    source: string;
    phone: string;
    email: string;
    contact_id: number | string;
    extra_fields: Record<string, any> | null;
  } {
    const name =
      [contact?.first_name, contact?.last_name].filter(Boolean).join(' ').trim() ||
      contact?.contact_name ||
      contact?.name ||
      'Contact';

    return {
      name: String(name).trim(),
      source: contact?.source || contact?.lead_source || 'crm',
      phone: String(contact?.mobile || contact?.phone || '').trim(),
      email: String(contact?.email || '').trim(),
      contact_id: contact?.contact_id,
      extra_fields: normalizeExtraFields(contact?.fields),
    };
  }
}
